import {
  useHotkey as useTanstackHotkey,
  type RegisterableHotkey,
  type UseHotkeyOptions as TanstackUseHotkeyOptions,
} from "@tanstack/react-hotkeys";

/**
 * A keyboard shortcut in platform-neutral form.
 *
 * Write `Mod` for the primary accelerator — it resolves to Command on Apple
 * platforms and Control everywhere else, so a binding is authored once
 * (`"Mod+K"`) rather than branched per platform. `Control` and `Command`
 * remain available when a shortcut genuinely must be platform-specific.
 */
export type Hotkey = RegisterableHotkey;

/**
 * Behavioral options for {@link useHotkey}. A deliberately narrow slice of
 * the underlying library's surface — widen it here, not at call sites, so
 * the seam stays thin enough to swap.
 */
export interface UseHotkeyOptions {
  /**
   * Suppresses firing while keeping the registration alive (so it still
   * appears in devtools and conflict detection). Prefer this to
   * conditionally calling the hook, which breaks the rules of hooks.
   * @default true
   */
  enabled?: boolean;
  /**
   * Whether to fire while focus is inside a text input.
   *
   * Left unset, the library decides per shortcut: Ctrl/Meta combinations and
   * Escape fire inside inputs, while bare keys and Shift/Alt combinations do
   * not — so `Mod+K` opens a palette from inside a search box, but a bare `G`
   * never hijacks typing. Override only when a shortcut needs to defy that.
   */
  ignoreInputs?: boolean;
  /**
   * Calls `preventDefault` on the matching event, which is what stops the
   * browser's own binding (e.g. Ctrl+K focusing the address bar).
   * @default true
   */
  preventDefault?: boolean;
}

/**
 * Registers an application keyboard shortcut.
 *
 * The design-system wrapper around TanStack Hotkeys. Feature code imports
 * this rather than `@tanstack/react-hotkeys` directly, per the foundation
 * convention: the library is pre-1.0, so containing it to one module keeps a
 * breaking upstream change a one-file edit instead of a call-site sweep.
 *
 * Registrations are pooled in a single document-level listener, so N hotkeys
 * cost one event handler rather than N. Conflicting registrations warn in the
 * console rather than silently shadowing one another.
 *
 * The callback may close over state freely — it is re-synced every render, so
 * it never goes stale.
 *
 * @example
 * ```ts
 * useHotkey("Mod+K", () => setPaletteOpen(true), { enabled: isEnabled });
 * ```
 */
export function useHotkey(
  hotkey: Hotkey,
  callback: (event: KeyboardEvent) => void,
  options: UseHotkeyOptions = {},
): void {
  // Passed straight through rather than enumerated field by field. The
  // library merges `{ ...defaults, ...options }`, so naming a field the
  // caller omitted would spread an explicit `undefined` over the default and
  // silently win — `enabled: undefined` reads as falsy and the hotkey never
  // fires. Forwarding the caller's object leaves omitted keys absent, so the
  // defaults apply. `UseHotkeyOptions` still bounds the surface: it is a
  // strict subset of the library's, and excess-property checking rejects
  // anything outside it at the call site.
  const tanstackOptions: TanstackUseHotkeyOptions = options;

  useTanstackHotkey(hotkey, (event) => callback(event), tanstackOptions);
}
